/**
 * Content for the Pricing tab (PRD §8) — the two service cards, the
 * feature-comparison matrix, and Appendix D's 20-question FAQ.
 * Kept in one module so the Pricing layout stays a straightforward
 * data-driven render.
 */

export interface ServiceCard {
  id: 'cloud' | 'on-prem';
  title: string;
  tagline: string;
  description: string;
  included: string[];
  notIncluded: string[];
  ctaSubject: string;
  ctaBodyIntro: string;
  emphasise?: boolean;
}

export const SERVICE_CARDS: ServiceCard[] = [
  {
    id: 'cloud',
    title: 'Cloud service',
    tagline: 'DI Deck managed on your cloud project — zero infrastructure overhead.',
    description:
      'DI Deck deployed to a tenant\'s cloud project (GCP / AWS / Azure) or, at pilot, to the vendor cloud in the tenant\'s chosen region. Managed deploy pipeline (versioned images, automatic upgrades on the tenant\'s cadence). Suitable for tenants who want zero infrastructure overhead and are comfortable with sovereign-cloud residency guarantees.',
    included: [
      'The Cockpit + Mobile Web App — every application, EN + HE (RTL)',
      'Sovereign Proxy with the eight ship-day connectors',
      'Immutable Ledger with signed export + optional S3 Object Lock anchor',
      'Managed deploy pipeline — versioned images, automatic upgrades on your cadence',
      'Sovereign Machine Learning Layer — S0 substrate included, S1–S5 opt-in per component',
      'DPA availability + EU AI Act conformity file generation',
      '99.9 % monthly-uptime SLA on the default service tier',
    ],
    notIncluded: [
      'Cloud account setup (your GCP / AWS / Azure organisation)',
      'IdP wiring on your side (Okta / Azure AD / Google Workspace admin work)',
      'GPU procurement if you enable Machine Learning Layer Stage S4 (on-tenant LLM)',
      'Third-party model provider accounts (Anthropic, OpenAI, etc.)',
    ],
    ctaSubject: 'DI Deck — cloud service pilot enquiry',
    ctaBodyIntro:
      'Interested in a tailored cloud-service pilot of DI Deck. A short qualification call to confirm fit would be helpful.',
  },
  {
    id: 'on-prem',
    title: 'On-premise service',
    tagline: 'DI Deck on your hardware, in your data centre, air-gap supported.',
    description:
      'DI Deck deployed on tenant-owned hardware in the tenant data centre (Kubernetes or bare-metal VMs). Air-gapped supported. Base-model weights arrive on approved media; the LLM sidecar verifies by hash before load. Regulator export produced on tenant-managed physical media. Suitable for defence, national-security, and sovereign-mandate tenants.',
    included: [
      'The Cockpit + Mobile Web App — every application, EN + HE (RTL)',
      'Sovereign Proxy with the eight ship-day connectors',
      'Immutable Ledger with signed export + optional on-prem WORM anchor',
      'Sovereign Machine Learning Layer — S0 substrate + all opt-in stages, air-gap-compatible',
      'Air-gap deployment pattern — no external egress required',
      'Weight-load verification by hash — supply-chain integrity built in',
      'DPA availability + EU AI Act conformity file generation',
    ],
    notIncluded: [
      'Tenant-side hardware (compute, storage, network)',
      'GPU procurement for Stage S4 (on-tenant LLM)',
      'On-prem IdP configuration',
      'Physical media handling for weight-load + regulator export',
    ],
    ctaSubject: 'DI Deck — on-prem service pilot enquiry',
    ctaBodyIntro:
      'Interested in a tailored on-premise pilot of DI Deck. Deployment shape (Kubernetes vs bare-metal, air-gap requirements) will need scoping.',
  },
];

/**
 * ~15-row feature comparison matrix (PRD §8.2 bullet 5).
 * Cell values: string (label) OR true (included) OR false (excluded).
 */
export interface MatrixRow {
  section?: string;
  feature: string;
  cloud: string | boolean;
  onPrem: string | boolean;
  note?: string;
}

export const FEATURE_MATRIX: MatrixRow[] = [
  { section: 'Cockpit + Mobile', feature: 'All Cockpit apps (Insights, Missions, Decisions, Ledger, Admin, …)', cloud: true, onPrem: true },
  { feature: 'Mobile PWA — same routing contract as desktop', cloud: true, onPrem: true },
  { feature: 'EN + HE (RTL) shipping today', cloud: true, onPrem: true },

  { section: 'Sovereign Machine Learning Layer', feature: 'S0 Foundations (Registry + Runner + 8 ledger classes)', cloud: true, onPrem: true },
  { feature: 'S1 On-tenant embeddings', cloud: 'opt-in', onPrem: 'opt-in' },
  { feature: 'S2 KPI anomaly detection', cloud: 'opt-in', onPrem: 'opt-in' },
  { feature: 'S3 Learned Mission Control ranker', cloud: 'opt-in', onPrem: 'opt-in' },
  { feature: 'S4 On-tenant sovereign LLM (requires GPU)', cloud: 'opt-in', onPrem: 'opt-in' },
  { feature: 'S5 Routing-policy learner', cloud: 'opt-in', onPrem: 'opt-in' },

  { section: 'Connectors (ship-day)', feature: 'Outlook 365 (mail + calendar)', cloud: true, onPrem: true },
  { feature: 'Slack, Teams', cloud: true, onPrem: true },
  { feature: 'Salesforce, Jira, Power BI, DocuSign', cloud: true, onPrem: true },

  { section: 'Deployment + upgrades', feature: 'Deployment topology', cloud: 'GCP / AWS / Azure', onPrem: 'K8s or bare-metal, tenant DC' },
  { feature: 'TLS handling', cloud: 'Cloudflare-managed edge', onPrem: 'Tenant-managed (any CA)' },
  { feature: 'Upgrade cadence', cloud: 'Automatic, tenant-picks-window', onPrem: 'Tenant-triggered, versioned images' },
  { feature: 'Air-gap operation', cloud: false, onPrem: true },

  { section: 'Governance', feature: 'DPA availability', cloud: true, onPrem: true },
  { feature: 'EU AI Act conformity file generation', cloud: true, onPrem: true },
  { feature: 'External ledger anchor (WORM)', cloud: 'S3 Object Lock', onPrem: 'S3 Object Lock or tenant-managed' },
  { feature: 'Default SLA', cloud: '99.9 % monthly uptime', onPrem: 'Scoped per pilot contract' },
];

/**
 * The 20-question FAQ from PRD Appendix D. Rendered as native
 * <details>/<summary> so no JS state is needed and the page scores
 * well on Lighthouse.
 */
export interface FaqEntry {
  q: string;
  a: string;
}

export const FAQ: FaqEntry[] = [
  {
    q: 'What is DI Deck in one sentence?',
    a: 'A sovereign Decision Orchestration Platform deployed inside your own infrastructure. It sits on top of your existing enterprise stack, unifying every decision, draft, meeting, and AI call into one signature-aware, ledger-anchored fabric — so the organisation acts as one. Sovereignty is built into the substrate: every AI call is redaction-normalised at egress, every decision is anchored in a hash-chained ledger you own.',
  },
  {
    q: 'How does DI Deck help my organisation act as one?',
    a: 'By making every decision object — insight, draft, signature, commit — flow through the same fabric. Managers work in one Cockpit with the same objects their reports see. Missions carry typed Goals and KPIs that update in real time as decisions commit. Signature Rules per mission class encode who commits what once and enforce them across every surface. The CEO\'s view and the regional VP\'s view converge on the same objects — no drift between what leadership sees and what the field executes.',
  },
  {
    q: 'How does DI Deck keep my organisation on track against its Goals and KPIs?',
    a: 'Every decision a manager commits re-attributes its projected impact to the mission\'s Goals and KPIs — RAG bands update in real time in the same Cockpit view. When a target starts to drift, the Machine Learning Layer fires a pre-threshold signal ≥ 24 samples before the band trips, Priority Insights ranks the manager\'s next-best action, and the Mission Control ranker surfaces it into the day\'s queue. Staying on track is the platform\'s job — the manager\'s job is to pick and sign.',
  },
  {
    q: 'How is sovereignty enforced technically — not just contractually?',
    a: 'The Sovereign Proxy is the single egress point for every AI call. It runs inside your infrastructure, applies redaction before egress, and anchors the call in your ledger. The DI Deck vendor has no backend that receives your traffic; the architecture makes vendor access structurally impossible, not merely policy-forbidden.',
  },
  {
    q: 'Does the DI Deck vendor see any of my data?',
    a: 'No. All tenant data — prompts, source records, decisions, model weights, embeddings, ledger entries — lives inside your infrastructure. The vendor has no read path. Optional telemetry (aggregate SLO metrics only, no data or prompts) is off by default and requires an admin opt-in.',
  },
  {
    q: 'Which AI models does DI Deck work with?',
    a: 'Anthropic Claude, OpenAI GPT, Google Gemini, Azure OpenAI, AWS Bedrock, Cohere, Mistral — routable via the Model Router with per-mission-class × sensitivity rules. Plus tenant-VPC-hosted models and on-prem small LLMs (Llama 3.1, Mistral, Phi, Qwen) via the Sovereign Machine Learning Layer\'s Stage S4.',
  },
  {
    q: 'Can DI Deck run air-gapped (no external egress at all)?',
    a: 'Yes. On-prem topology supports full air-gap operation. Base-model weights arrive on approved media, the LLM sidecar verifies by hash before load, and the Sovereign Proxy operates in "no external route" mode. Regulator export produced on tenant-managed physical media.',
  },
  {
    q: 'How does the Immutable Ledger help my regulator?',
    a: 'Every consequential action is a hash-chained ledger entry with actor identity, subject, inputs, and outcome. Signed export bundles ship with an independent verifier that reruns the chain from Genesis without needing access to your platform. Optional external anchor to an S3 Object-Lock (WORM) target gives regulator-grade evidence.',
  },
  {
    q: 'Is DI Deck EU AI Act compliant?',
    a: 'DI Deck is built on the shape the Act asks for. Articles 6, 9, 10, 12, 13, 14, and 15 all map to concrete platform capabilities the code makes good on — see the EU AI Act tab and the companion DI-Deck-EU-AI-Act-Mitigation.docx for the full article-by-article mapping. DPIA templates auto-populate from your tenant configuration.',
  },
  {
    q: 'Which connected sources are supported today?',
    a: 'Eight connectors ship: Outlook 365 (mail + calendar), Slack, Teams, Salesforce, Jira, Power BI, DocuSign. Additional connectors on the near-term roadmap: SAP S/4HANA, Priority ERP, HubSpot, Guidewire, NetSuite, Tableau, ServiceNow, GitHub Enterprise. The connector runtime uses a uniform CanonicalRecord interface, so adding a new source is config, not a new codebase.',
  },
  {
    q: 'Can I bring my own AI models?',
    a: 'Yes. The Model Router accepts any endpoint that implements the OpenAI-compatible or Anthropic-compatible wire format. Register the endpoint with its contract descriptor (training-use-forbidden, data-retention-days) and it becomes a routing target the platform selects per your policy.',
  },
  {
    q: 'What happens to my data if I stop using DI Deck?',
    a: 'You own the model weights, the ledger, the training data, and the vector index. On exit, DI Deck exports everything to a tenant-designated destination. There is no vendor-side data you need to reclaim — nothing was ever there.',
  },
  {
    q: 'How long does deployment take?',
    a: 'A full pilot cohort (~50 users) completes onboarding in about 2 weeks; the platform-side configuration is fast — the long pole is customer-side SSO wiring and connector app registration. A single user onboarded into a fully-configured tenant takes under 15 minutes.',
  },
  {
    q: 'What team does my organisation need to operate DI Deck?',
    a: 'One Tenant Admin (typically the CIO or VP IT) for policy configuration + integrations; standard IT operations for platform uptime + SSO; no dedicated Machine Learning team required for Stages S0-S3 + S5 of the Machine Learning Layer. Stage S4 (on-tenant LLM) benefits from a part-time Machine Learning operator once weekly training cycles start.',
  },
  {
    q: 'What GPU is required for the on-tenant LLM?',
    a: 'For a 7B/8B LoRA fine-tune (the recommended default): 1× H100 or A100 80GB for training (a shared regional resource is fine); 1× A10 24GB or L4 for serving one tenant. Smaller base models (Phi-3.5 Mini at 3.8B) run comfortably on A100 40GB for training and even more modest hardware for serving.',
  },
  {
    q: 'Can I run DI Deck without any GPUs?',
    a: 'Yes. The platform core, all Cockpit + Mobile applications, and Sovereign Machine Learning Layer stages S0/S2/S3/S5 run CPU-only. Stage S1 embeddings can also run CPU with the default hash backend for dev/test or a light sentence-transformer for production. Only Stage S4 (on-tenant LLM) requires a GPU, and it is opt-in.',
  },
  {
    q: 'How is user identity managed?',
    a: 'SSO via SAML 2.0 or OIDC (Okta, Azure AD, Google Workspace, and every other standard IdP). Session tokens signed with a per-tenant env-driven key; IP + user-agent binding on the session; scope-typed step-up MFA (WebAuthn / Touch ID / TOTP / push) required for every state-changing action.',
  },
  {
    q: 'What is the pricing model?',
    a: 'First customers onboard through a tailored pilot shaped to their operation. The standing service offerings — Cloud service and On-premise service — appear on the Pricing tab; each routes to sales@dideck.com for a scoping conversation. Automated Stripe checkout comes later, once first pilots have run and standing prices are calibrated.',
  },
  {
    q: 'Do you offer a trial?',
    a: 'The tailored pilot IS the trial — a scoped deployment against a real (subset of) operation with 30/60/90-day review checkpoints. It is a paid engagement so both sides commit to real work; the outcome is either contract conversion or a documented exit. A future in-browser sandbox will let prospects click-through the Cockpit without a pilot commitment.',
  },
  {
    q: 'Can I integrate DI Deck with my existing identity provider?',
    a: 'Yes. SAML 2.0 and OIDC both supported. Wiring is a one-hour job with your IdP admin during Stage 2 of the onboarding flow. The platform never stores a separate DI Deck password — every user signs in through your IdP with whatever MFA policy you already enforce.',
  },
  {
    q: 'How does step-up MFA work?',
    a: 'Every state-changing action (approve a decision, sign a Do-It, promote a model, disable the on-tenant LLM, suppress an anomaly signal, accept a routing proposal) requires a scope-specific step-up token issued via WebAuthn / Touch ID / TOTP / push. A token for one scope does not grant others. Every step-up event is a ledger entry.',
  },
  {
    q: 'What SLAs do you offer?',
    a: 'Cloud service default SLA: 99.9 % monthly uptime, 24-hour security-advisory notification, priority support in local business hours. Business + Enterprise tiers add 24/7 support and higher-tier SLAs negotiated per contract. On-premise SLAs are scoped in the pilot contract — the platform runs on the tenant\'s hardware so vendor SLA covers the platform code, not the underlying infrastructure.',
  },
];

/**
 * Build a prefilled mailto: URL for a service-card CTA.
 * Body follows PRD §8.2 — organisation, headcount band, sector,
 * jurisdiction, preferred timeline.
 */
export function buildMailto(card: ServiceCard): string {
  const bodyLines = [
    card.ctaBodyIntro,
    '',
    'Organisation:',
    'Headcount band (choose one: <100 / 100–500 / 500–2000 / 2000–10000 / 10000+):',
    'Sector:',
    'Primary jurisdiction (EU / UK / US / APAC / MENA / other):',
    'Preferred pilot start timeline (choose one: this quarter / next quarter / H1 next year / exploring):',
    '',
    'Anything specific to share up-front:',
    '',
    '(Sent from https://dideck.com/pricing)',
  ];
  const params = new URLSearchParams({
    subject: card.ctaSubject,
    body: bodyLines.join('\n'),
  });
  return `mailto:sales@dideck.com?${params.toString().replace(/\+/g, '%20')}`;
}
